import React from 'react';
import JsonLd, { faqSchema } from './JsonLd';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { useScrollReveal } from '../hooks/useScrollReveal';

/**
 * Reusable FAQ block with FAQPage JSON-LD (GEO: AI engines quote FAQs heavily).
 */
const FAQSection = ({ eyebrow = 'FAQ', title, highlight, subtitle, faqs, id = 'faq' }) => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <section id={id} className="py-20 lg:py-28 bg-white" data-testid={`${id}-section`}>
      <JsonLd data={faqSchema(faqs)} />
      <div ref={ref} className={`section-container reveal ${isVisible ? 'visible' : ''}`}>
        <div className="max-w-3xl mb-12">
          <span
            className="inline-block text-sm font-semibold text-[#00BCE4] uppercase tracking-[3px] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {eyebrow}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {title} <span className="text-[#00BCE4]">{highlight}</span>
          </h2>
          {subtitle && (
            <p className="text-base md:text-lg text-gray-600 mt-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              {subtitle}
            </p>
          )}
        </div>
        <Accordion type="single" collapsible className="max-w-4xl space-y-4">
          {faqs.map((faq, i) => (
            <AccordionItem
              key={faq.q}
              value={`${id}-${i}`}
              data-testid={`${id}-item-${i}`}
              className="bg-gray-50 border border-gray-200 rounded-2xl overflow-hidden px-6 hover:border-[#00BCE4]/40 transition-colors duration-300 data-[state=open]:border-[#00BCE4] data-[state=open]:bg-white data-[state=open]:shadow-lg data-[state=open]:shadow-[#00BCE4]/5"
            >
              <AccordionTrigger className="py-5 hover:no-underline text-left">
                <h3
                  className="text-base font-bold text-gray-900"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {faq.q}
                </h3>
              </AccordionTrigger>
              <AccordionContent className="pb-6 pt-0">
                <p
                  className="text-base text-gray-600 leading-relaxed"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {faq.a}
                </p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default FAQSection;
